import type { BadgeId } from './badges';

export type ShotOutcome = 'goal' | 'saved' | 'off';

export interface ShareGridInput {
  puzzleNumber: number;
  outcomes: readonly ShotOutcome[]; // any length ≥ 0; v1=5, v2=7
  streak: number;
  domain: string;
  /** HU display string for streak suffix; defaults to "napos sorozat" if not provided. */
  streakLabel?: string;
  /** Optional badge line — caller resolves localized names. Pass [] or omit to skip. */
  badges?: ReadonlyArray<{ id: BadgeId; emoji: string; name: string }>;
  /** Optional leading token on the title line (e.g. '🕶️' hard-mode marker). */
  shareMarker?: string;
}

/**
 * Formats the Wordle-style share string. See spec §2.6.
 * The string is plain text and renders identically on Twitter/X, WhatsApp,
 * iMessage, Discord, Reddit, and most other social surfaces.
 *
 * Length-flex: renders one square per outcome regardless of length, so the
 * same function handles both legacy v1 (5 shots) and v2 (7 shots) rounds.
 */
export function formatShareGrid(input: ShareGridInput): string {
  const squares = input.outcomes.map((o) => (o === 'goal' ? '🟩' : '⬛')).join('');
  const score = input.outcomes.filter((o) => o === 'goal').length;
  const baseTitle = input.puzzleNumber > 0
    ? `Napi 11-es #${input.puzzleNumber} ⚽`
    : `Napi 11-es ⚽`;
  const titleLine = input.shareMarker ? `${input.shareMarker} ${baseTitle}` : baseTitle;
  const lines = [
    titleLine,
    `${squares}  ${score}/${input.outcomes.length}`,
  ];

  if (input.streak >= 3) {
    const label = input.streakLabel ?? 'napos sorozat';
    lines.push(`🔥 ${input.streak} ${label}`);
  }

  if (input.badges && input.badges.length > 0) {
    const emojis = input.badges.map((b) => b.emoji).join(' ');
    const names = input.badges.map((b) => b.name).join(' · ');
    lines.push(`${emojis} ${names}`);
  }

  lines.push(input.domain);
  return lines.join('\n');
}

/**
 * Plan #1 §4.1 — 🕶️ marker is earned only on a CLEAN hard run:
 * hard mode AND no assist consumed. Reads the local-only DailyRecord fields.
 */
export function deriveShareMarker(
  record: { mode?: 'daily' | 'daily-hard'; assistUsed?: boolean },
): '🕶️' | undefined {
  return record.mode === 'daily-hard' && record.assistUsed !== true ? '🕶️' : undefined;
}
